// Histórico de um contrato PJ (ex-aba Movimentacoes) e as ações sobre ele: reajuste, troca de CNPJ,
// outra alteração, corrigir/excluir movimentação e (re)gerar o aditivo de reajuste.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightLeft, ExternalLink, FileText, Pencil, Plus, RefreshCw, Trash2, TrendingUp, UserMinus } from "lucide-react";
import {
  deleteRhMovimentacao, getRhConsultaCnpj, getRhHistoricoContrato, postRhAlteracaoCnpj, postRhGerarAditivo,
  postRhOutraMovimentacao, postRhReajuste, putRhMovimentacao,
} from "../../api";
import { formatData, hojeISO } from "../estoque/comum";
import { AvisoErro, Campo, Modal, NumeroInput, btnStyle, inputStyle, numeroParaInput, parseNumero } from "../estoque/ui";
import { useFormatValor } from "./valores";
import DesligamentoModal from "./DesligamentoModal";
import { mascaraCnpj } from "./formularios";

const OUTROS_TIPOS = [
  { valor: "alteracao_vigencia", rotulo: "Alteração de vigência" },
  { valor: "alteracao_servico", rotulo: "Alteração de serviço" },
  { valor: "ajuste_comissao", rotulo: "Ajuste de comissão" },
  { valor: "ajuste_clausula", rotulo: "Ajuste de cláusula" },
  { valor: "alteracao_contratada", rotulo: "Alteração da contratada" },
  { valor: "alteracao_contratante", rotulo: "Alteração da contratante" },
];

function CaixaGerar({ C, valor, onChange, documentosDisponiveis }) {
  return (
    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 12, color: documentosDisponiveis ? C.text : C.muted }}>
      <input type="checkbox" checked={valor} onChange={(e) => onChange(e.target.checked)} disabled={!documentosDisponiveis} style={{ marginTop: 3 }} />
      <span>
        Gerar o aditivo no Google Docs agora
        {!documentosDisponiveis && <span style={{ display: "block", fontSize: 11 }}>Indisponível: os modelos do Drive ainda não estão acessíveis. Registre agora e gere o aditivo depois.</span>}
      </span>
    </label>
  );
}

/** Formulário da ação escolhida: reajuste | cnpj | outra | editar (movimentação). */
function FormAcao({ C, acao, contrato, mov, documentosDisponiveis, onCancelar, onConcluir }) {
  const [form, setForm] = useState(() => ({
    valor: mov ? numeroParaInput(mov.valor_mensal) : "",
    data: mov ? mov.data_vigencia : hojeISO(),
    descricao: mov?.descricao || "",
    tipo: OUTROS_TIPOS[0].valor,
    cnpj: "", razao_social: "", natureza_juridica: "",
    gerar: documentosDisponiveis && acao !== "outra",
  }));
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const set = (dados) => setForm((f) => ({ ...f, ...dados }));

  async function consultarCnpj() {
    setErro("");
    try {
      const r = await getRhConsultaCnpj(form.cnpj);
      set({ cnpj: r.cnpj, razao_social: r.razao_social, natureza_juridica: r.natureza_juridica });
    } catch (e) {
      setErro(e.message);
    }
  }

  async function salvar() {
    setErro("");
    const valor = parseNumero(form.valor);
    if (!form.data) return setErro("Informe a data de vigência.");
    if (acao === "reajuste" && !valor) return setErro("Informe o novo valor mensal.");
    setSalvando(true);
    try {
      let r;
      if (acao === "reajuste") {
        r = await postRhReajuste(contrato.id, { valor_mensal: valor, data_vigencia: form.data, descricao: form.descricao || null, gerar_documento: form.gerar });
      } else if (acao === "cnpj") {
        r = await postRhAlteracaoCnpj(contrato.id, {
          cnpj: form.cnpj, razao_social: form.razao_social || null, natureza_juridica: form.natureza_juridica || null,
          data_vigencia: form.data, descricao: form.descricao || null, gerar_documento: form.gerar,
        });
      } else if (acao === "outra") {
        r = await postRhOutraMovimentacao(contrato.id, { tipo: form.tipo, data_vigencia: form.data, valor_mensal: valor, descricao: form.descricao || null });
      } else {
        r = await putRhMovimentacao(mov.id, { data_vigencia: form.data, valor_mensal: valor, descricao: form.descricao || null });
      }
      onConcluir(r?.documento);
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  const titulos = { reajuste: "Novo reajuste de valor", cnpj: "Troca de CNPJ", outra: "Outra alteração contratual", editar: "Corrigir movimentação" };
  return (
    <div style={{ border: `1px solid ${C.brand}`, borderRadius: 12, padding: 14, marginBottom: 14, background: C.bgActive }}>
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>{titulos[acao]}</div>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
        {acao === "outra" && (
          <Campo C={C} label="Tipo" obrigatorio>
            <select value={form.tipo} onChange={(e) => set({ tipo: e.target.value })} style={inputStyle(C)}>
              {OUTROS_TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
            </select>
          </Campo>
        )}
        {acao === "cnpj" && (
          <Campo C={C} label="Novo CNPJ" obrigatorio>
            <div style={{ display: "flex", gap: 6 }}>
              <input value={form.cnpj} inputMode="numeric" onChange={(e) => set({ cnpj: mascaraCnpj(e.target.value) })} style={inputStyle(C)} />
              <button type="button" onClick={consultarCnpj} disabled={form.cnpj.replace(/\D/g, "").length !== 14} style={{ ...btnStyle(C.brand, true), padding: "0 10px" }}>Receita</button>
            </div>
          </Campo>
        )}
        <Campo C={C} label="Vigência a partir de" obrigatorio>
          <input type="date" value={form.data} onChange={(e) => set({ data: e.target.value })} style={inputStyle(C)} />
        </Campo>
        {acao !== "cnpj" && (
          <Campo C={C} label={acao === "reajuste" ? "Novo valor mensal (R$)" : "Valor mensal (R$)"} obrigatorio={acao === "reajuste"}
            ajuda={acao === "outra" ? "Em branco: mantém o valor atual" : null}>
            <NumeroInput C={C} casas={2} value={form.valor} onChange={(v) => set({ valor: v })} />
          </Campo>
        )}
        <Campo C={C} label="Descrição / motivo">
          <input value={form.descricao} onChange={(e) => set({ descricao: e.target.value })} placeholder={acao === "reajuste" ? "Reajuste de valor" : ""} style={inputStyle(C)} />
        </Campo>
      </div>
      {acao === "cnpj" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, marginTop: 12 }}>
          <Campo C={C} label="Nova razão social" ajuda="Em branco: mantém a atual">
            <input value={form.razao_social} onChange={(e) => set({ razao_social: e.target.value })} style={inputStyle(C)} />
          </Campo>
          <Campo C={C} label="Nova natureza jurídica" ajuda="Em branco: mantém a atual">
            <input value={form.natureza_juridica} onChange={(e) => set({ natureza_juridica: e.target.value })} style={inputStyle(C)} />
          </Campo>
        </div>
      )}
      {acao === "editar" && mov.documentos.length > 0 && (
        <p style={{ fontSize: 12, color: C.muted, margin: "10px 0 0" }}>O aditivo já gerado não muda. Depois de corrigir, use “Gerar aditivo de novo”.</p>
      )}
      {(acao === "reajuste" || acao === "cnpj") && (
        <CaixaGerar C={C} valor={form.gerar} onChange={(v) => set({ gerar: v })} documentosDisponiveis={documentosDisponiveis} />
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button onClick={onCancelar} style={btnStyle(C.muted, true)}>Cancelar</button>
        <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>
          {salvando ? (form.gerar ? "Gerando aditivo…" : "Salvando…") : "Salvar"}
        </button>
      </div>
    </div>
  );
}

function LinkDocumento({ C, doc }) {
  const rotulo = doc.tipo === "termo_rescisao" ? "Termo de rescisão"
    : doc.formato === "google_doc" ? "Aditivo (Docs)" : doc.formato === "drive_arquivo" ? "Arquivo assinado" : doc.nome;
  if (!doc.url) return <span title={doc.nome} style={{ fontSize: 12, color: C.muted }}><FileText size={12} style={{ verticalAlign: "-2px" }} /> {rotulo}</span>;
  return (
    <a href={doc.url} target="_blank" rel="noreferrer" title={doc.nome || doc.url} style={{ fontSize: 12, color: C.brand, fontWeight: 600, textDecoration: "none", display: "inline-flex", gap: 4, alignItems: "center" }}>
      <ExternalLink size={12} /> {rotulo}
    </a>
  );
}

export default function ContratoPjModal({ C, contratoId, documentosDisponiveis, onClose, onAlterado }) {
  const formatBRL = useFormatValor();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState(null); // { texto, url }
  const [acao, setAcao] = useState(null); // null | "reajuste" | "cnpj" | "outra" | { editar: mov }
  const [ocupado, setOcupado] = useState(null); // id da movimentação em operação
  const [desligando, setDesligando] = useState(false);

  const carregar = useCallback(() => {
    getRhHistoricoContrato(contratoId).then(setDados).catch((e) => setErro(e.message));
  }, [contratoId]);
  useEffect(() => { carregar(); }, [carregar]);

  function concluido(texto, documento) {
    setAcao(null);
    setAviso(documento ? { texto: `${texto} ${documento.nome} gerado.`, url: documento.url,
      extra: documento.tags_sobrando?.length ? `Atenção: o modelo tem tag não preenchida (${documento.tags_sobrando.join(", ")}).` : "" } : { texto });
    carregar();
    onAlterado();
  }

  async function gerar(mov) {
    setOcupado(mov.id);
    setErro("");
    try {
      concluido("Aditivo gerado:", await postRhGerarAditivo(mov.id));
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(null);
    }
  }

  async function excluir(mov) {
    if (!window.confirm(`Excluir a movimentação de ${formatData(mov.data_vigencia)}? O documento no Drive não é apagado.`)) return;
    setOcupado(mov.id);
    setErro("");
    try {
      setDados(await deleteRhMovimentacao(mov.id));
      setAviso({ texto: "Movimentação excluída." });
      onAlterado();
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(null);
    }
  }

  const contrato = dados?.contrato;
  const ultimoNumero = dados ? dados.proximo_aditivo - 1 : null;
  const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: C.muted, borderBottom: `1px solid ${C.border}` };
  const td = { padding: "8px 10px", fontSize: 13, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" };

  return (
    <Modal C={C} titulo={contrato ? `${contrato.nome} · ${contrato.cnpj_prestador || "sem CNPJ"}` : "Contrato PJ"} onClose={onClose} largura={1000}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {aviso && (
        <div role="status" style={{ background: `${C.success}18`, border: `1px solid ${C.success}`, color: C.success, borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
          {aviso.texto} {aviso.url && <a href={aviso.url} target="_blank" rel="noreferrer" style={{ color: C.success, fontWeight: 700 }}>Abrir documento</a>}
          {aviso.extra && <div style={{ color: C.warning, marginTop: 4 }}>{aviso.extra}</div>}
        </div>
      )}
      {!dados ? (
        !erro && <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <button onClick={() => setAcao("reajuste")} disabled={contrato.status !== "Ativo"} style={{ ...btnStyle(C.brand), opacity: contrato.status === "Ativo" ? 1 : 0.5 }}><TrendingUp size={15} /> Reajuste</button>
            <button onClick={() => setAcao("cnpj")} style={btnStyle(C.brand, true)}><ArrowRightLeft size={15} /> Troca de CNPJ</button>
            <button onClick={() => setAcao("outra")} style={btnStyle(C.brand, true)}><Plus size={15} /> Outra alteração</button>
            {contrato.status === "Ativo" && <button onClick={() => setDesligando(true)} style={btnStyle(C.danger, true)}><UserMinus size={15} /> Desligar</button>}
            <button onClick={() => navigate(`/rh/colaboradores/${contrato.colaborador_id}`)} style={{ ...btnStyle(C.muted, true), marginLeft: "auto" }}>Abrir cadastro</button>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Próximo aditivo: nº {dados.proximo_aditivo}{contrato.status !== "Ativo" && " · contrato inativo"}</div>

          {acao && (
            <FormAcao key={typeof acao === "string" ? acao : `editar-${acao.editar.id}`} C={C} contrato={contrato}
              acao={typeof acao === "string" ? acao : "editar"} mov={acao.editar} documentosDisponiveis={documentosDisponiveis}
              onCancelar={() => setAcao(null)}
              onConcluir={(doc) => concluido(acao.editar ? "Movimentação corrigida." : "Movimentação registrada.", doc)} />
          )}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>Vigência</th><th style={th}>Movimentação</th><th style={{ ...th, textAlign: "right" }}>Valor mensal</th><th style={th}>Nº</th><th style={th}>Documentos</th><th style={th} /></tr>
              </thead>
              <tbody>
                {dados.movimentacoes.length === 0 && <tr><td colSpan={6} style={{ ...td, color: C.muted, textAlign: "center", padding: 20 }}>Sem histórico. O primeiro reajuste abre o histórico dos aditivos.</td></tr>}
                {dados.movimentacoes.map((m) => (
                  <tr key={m.id}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(m.data_vigencia)}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{m.tipo_descricao}</div>
                      {m.descricao && m.descricao !== m.tipo_descricao && <div style={{ fontSize: 12, color: C.muted }}>{m.descricao}</div>}
                      {m.cnpj_anterior && <div style={{ fontSize: 12, color: C.muted }}>CNPJ anterior: {m.cnpj_anterior}</div>}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{formatBRL(m.valor_mensal)}</td>
                    <td style={td}>{m.numero_aditivo ?? "—"}</td>
                    <td style={td}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {m.documentos.map((d) => <LinkDocumento key={d.id} C={C} doc={d} />)}
                        {m.documentos.length === 0 && <span style={{ fontSize: 12, color: m.tipo === "reajuste_valor" ? C.warning : C.muted }}>{m.tipo === "reajuste_valor" ? "sem aditivo" : "—"}</span>}
                      </div>
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap", textAlign: "right" }}>
                      {m.tipo === "reajuste_valor" && m.numero_aditivo && documentosDisponiveis && (
                        <button onClick={() => gerar(m)} disabled={ocupado === m.id} title={m.documentos.length ? "Gerar aditivo de novo" : "Gerar aditivo"} style={{ background: "none", border: "none", color: C.brand, cursor: "pointer", padding: 4 }}>
                          <RefreshCw size={15} className={ocupado === m.id ? "animate-spin" : ""} />
                        </button>
                      )}
                      <button onClick={() => setAcao({ editar: m })} title="Corrigir" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 4 }}><Pencil size={15} /></button>
                      {(m.numero_aditivo === null || m.numero_aditivo === ultimoNumero) && (
                        <button onClick={() => excluir(m)} disabled={ocupado === m.id} title="Excluir" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 4 }}><Trash2 size={15} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {desligando && (
            <DesligamentoModal C={C} contrato={contrato} nome={contrato.nome} onClose={() => setDesligando(false)}
              onConcluido={(r) => { setDesligando(false); concluido(`Desligamento registrado (${r.tipo.toLowerCase()}).`, r.documento); }} />
          )}
        </>
      )}
    </Modal>
  );
}
